import { useEffect, useId, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowRight, Check, Loader2, X } from "lucide-react";

import { ApiError, marketingApi, type SignupPopup as Popup } from "@/lib/api";
import {
  popupAllowedOn,
  popupKey,
  popupSuppressed,
  rememberPopupDismissed,
  rememberPopupSubscribed,
} from "@/lib/signup-popup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/ui/Logo";
import { trackSignupPopup } from "@/lib/tracking";

/**
 * The storefront popup, authored in the admin under "Signup popup". It asks
 * for an email, sends the visitor somewhere (the Paket Pembukaan's "Pilih
 * meja"), or both.
 *
 * The delay counts from landing, not from each page: it is started once, when
 * the shell mounts. If it runs out while the visitor is on the cart or at
 * checkout, the popup waits until they are somewhere else. Everything here is
 * client-only; the prerendered HTML never contains it.
 */
export function SignupPopup() {
  const { pathname } = useLocation();
  const [popup, setPopup] = useState<Popup | null>(null);
  const [due, setDue] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    marketingApi
      .signupPopup()
      .then((p) => {
        // Asked only once the popup is known: whether this browser has dealt
        // with it depends on which campaign it is and whether it wants an email.
        if (cancelled || !p.enabled || popupSuppressed(identity(p))) return;
        setPopup(p);
        timer = setTimeout(() => setDue(true), p.delaySeconds * 1000);
      })
      // No popup is the right answer to an API that is down.
      .catch(() => {});
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    // Re-checked here as well: another tab may have signed up in the meantime.
    if (due && !open && popup && popupAllowedOn(pathname) && !popupSuppressed(identity(popup))) {
      setOpen(true);
      setDue(false);
      trackSignupPopup("shown", popupKey(popup));
    }
  }, [due, open, pathname, popup]);

  if (!popup) return null;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          rememberPopupDismissed(popupKey(popup));
          trackSignupPopup("dismissed", popupKey(popup));
        }
        setOpen(o);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:fade-in" />
        <DialogPrimitive.Content
          data-lenis-prevent
          className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white shadow-2xl focus:outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
        >
          <div className={popup.imageUrl ? "grid md:grid-cols-[45%_1fr]" : ""}>
            {popup.imageUrl && (
              <img src={popup.imageUrl} alt="" className="hidden h-full min-h-[28rem] w-full object-cover md:block" />
            )}
            <PopupBody
              popup={popup}
              onDone={() => setOpen(false)}
              onFollow={() => {
                // Following the link is as good as closing it: this campaign
                // has done its job in this browser.
                rememberPopupDismissed(popupKey(popup));
                trackSignupPopup("link_followed", popupKey(popup));
                setOpen(false);
              }}
            />
          </div>
          <DialogPrimitive.Close className="absolute right-3 top-3 rounded-full p-2 text-ink/50 transition-colors hover:bg-ink/[0.05] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25">
            <X className="size-5" />
            <span className="sr-only">Tutup</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function identity(p: Popup) {
  return { key: popupKey(p), collectEmail: p.collectEmail };
}

/**
 * The popup's link, as a button: in-app for a storefront path, a new tab for
 * anywhere else.
 */
function PopupLink({
  popup,
  variant,
  onFollow,
}: {
  popup: Popup;
  variant: "default" | "outline";
  onFollow: () => void;
}) {
  const external = !popup.ctaHref.startsWith("/");
  return (
    <Button asChild size="lg" variant={variant} className="w-full">
      {external ? (
        <a href={popup.ctaHref} target="_blank" rel="noopener noreferrer" onClick={onFollow}>
          {popup.ctaLabel}
          <ArrowRight />
        </a>
      ) : (
        <Link to={popup.ctaHref} onClick={onFollow}>
          {popup.ctaLabel}
          <ArrowRight />
        </Link>
      )}
    </Button>
  );
}

function PopupBody({ popup, onDone, onFollow }: { popup: Popup; onDone: () => void; onFollow: () => void }) {
  const hasLink = Boolean(popup.ctaHref && popup.ctaLabel);
  const uid = useId();
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError("Isi alamat email kamu dulu ya.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await marketingApi.subscribe(email.trim());
      rememberPopupSubscribed();
      trackSignupPopup("submitted", popupKey(popup));
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ada yang tidak beres. Coba lagi sebentar ya.");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center sm:px-10">
      <Logo className="h-5 w-auto text-brand" />

      {done ? (
        <>
          <span className="mt-8 flex size-12 items-center justify-center rounded-full bg-brand-light text-brand">
            <Check className="size-6" />
          </span>
          <DialogPrimitive.Title className="mt-4 text-2xl font-medium tracking-tight text-ink">
            Makasih, kamu sudah terdaftar!
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-2 text-sm text-ink/60">
            Kabar produk baru dan musim diskon akan sampai ke {email.trim()}.
          </DialogPrimitive.Description>
          <Button className="mt-6 w-full" onClick={onDone}>
            Lanjut belanja
          </Button>
        </>
      ) : (
        <>
          <DialogPrimitive.Title className="mt-8 text-balance text-3xl font-medium leading-tight tracking-tight text-ink">
            {popup.title}
          </DialogPrimitive.Title>
          {popup.description ? (
            <DialogPrimitive.Description className="mt-3 text-pretty text-base text-ink/70">
              {popup.description}
            </DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="sr-only">Daftar dengan email</DialogPrimitive.Description>
          )}

          {!popup.collectEmail ? (
            hasLink && (
              <div className="mt-6 w-full">
                <PopupLink popup={popup} variant="default" onFollow={onFollow} />
              </div>
            )
          ) : (
            <form onSubmit={submit} className="mt-6 w-full space-y-3" noValidate>
              <label htmlFor={`${uid}-email`} className="sr-only">
                Alamat email
              </label>
              <Input
                id={`${uid}-email`}
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder="Alamat email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${uid}-error` : undefined}
              />
              {error && (
                <p id={`${uid}-error`} className="text-left text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" size="lg" className="w-full" disabled={pending}>
                {pending && <Loader2 className="animate-spin" />}
                {popup.buttonLabel}
              </Button>
              {hasLink && <PopupLink popup={popup} variant="outline" onFollow={onFollow} />}
            </form>
          )}

          {popup.collectEmail && popup.consentText && (
            <p className="mt-4 text-xs leading-relaxed text-ink/50">{popup.consentText}</p>
          )}
        </>
      )}
    </div>
  );
}
